import { useCallback, useEffect, useRef } from "react";
import type { Id } from "react-toastify";
import { toast } from "@shared/services/toast";

const PROGRESS_TICK_MS = 200;
const MAX_SIMULATED_PROGRESS = 0.99;

const nextSimulatedProgress = (current: number) => {
  const increment =
    current < 0.6
      ? Math.random() * 0.015 + 0.005
      : current < 0.8
        ? Math.random() * 0.006 + 0.002
        : Math.random() * 0.0025 + 0.0005;
  return Math.min(current + increment, MAX_SIMULATED_PROGRESS);
};

const renderProgress = (progress: number) =>
  `${progress < 0.6 ? "Generating PDF" : "Preparing PDF"}... ${Math.round(progress * 100)}%`;

export const usePdfGenerationToast = () => {
  const toastIdRef = useRef<Id | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (intervalRef.current == null) return;
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(() => {
    let progress = 0;
    toastIdRef.current = toast.loading(renderProgress(progress), {
      position: "bottom-right",
      progress,
      hideProgressBar: false,
    });
    intervalRef.current = setInterval(() => {
      progress = nextSimulatedProgress(progress);
      toast.update(toastIdRef.current as Id, {
        render: renderProgress(progress),
        progress,
      });
    }, PROGRESS_TICK_MS);
  }, []);

  const succeed = useCallback(() => {
    stop();
    if (toastIdRef.current == null) return;
    toast.update(toastIdRef.current, {
      render: "Your presentation PDF is ready",
      type: "success",
      isLoading: false,
      progress: null,
      autoClose: 5000,
      closeButton: true,
      closeOnClick: true,
      draggable: true,
    });
  }, [stop]);

  const fail = useCallback(() => {
    stop();
    if (toastIdRef.current == null) return;
    toast.update(toastIdRef.current, {
      render: "Failed to generate PDF. Please try again.",
      type: "error",
      isLoading: false,
      progress: null,
      autoClose: 5000,
      closeButton: true,
      closeOnClick: true,
      draggable: true,
    });
  }, [stop]);

  const run = useCallback(
    async <T>(action: () => Promise<T>): Promise<T> => {
      start();
      try {
        const result = await action();
        succeed();
        return result;
      } catch (error) {
        fail();
        throw error;
      }
    },
    [start, succeed, fail],
  );

  return { run };
};
